import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ComboboxOption {
  readonly id: string;
  readonly label: string;
}

/**
 * 可复用可搜索选择器（架构书 §5 "Combobox"；Spec §11：品牌/分类/产地/供应商/
 * 原因码共用同一套行为）：输入即搜（250ms debounce）→ 服务端已按"最常用/最近
 * 使用优先"排序 → 无结果时若调用方传了 `onCreate`（代表调用方已确认当前用户
 * 有权限）显示"+ 新建" → 键盘可用（↑↓ 选择、Enter 确认、Esc 关闭）。
 *
 * 受控组件：`value` 同时带 id 与 label，调用方（产品表单）已经从详情/列表拿到
 * 了名字，不需要这个组件自己反查一次。
 */
export function Combobox({
  value,
  onChange,
  fetchOptions,
  onCreate,
  placeholder,
  disabled,
}: {
  readonly value: ComboboxOption | null;
  readonly onChange: (option: ComboboxOption | null) => void;
  readonly fetchOptions: (query: string) => Promise<readonly ComboboxOption[]>;
  readonly onCreate?: (name: string) => Promise<ComboboxOption>;
  readonly placeholder: string;
  readonly disabled?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<readonly ComboboxOption[]>([]);
  const [highlighted, setHighlighted] = useState(0);
  const [creating, setCreating] = useState(false);
  /**
   * 防抖未结算标记（用户实测缺陷）：输入后 250ms 内按 Enter，`options` 还是
   * 上一次查询的结果，高亮 0 号会错选一个与输入毫不相干的旧选项（输入
   * "营养快线"却选中"康师傅"）。stale 期间 Enter 改走"立即查询再决定"。
   */
  const [stale, setStale] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchOptions(search).then((opts) => {
        setOptions(opts);
        setStale(false);
      });
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [search, open, fetchOptions]);

  useEffect(() => {
    if (open)
      void fetchOptions('').then((opts) => {
        setOptions(opts);
        setStale(false);
      });
  }, [open, fetchOptions]);

  const pick = (option: ComboboxOption): void => {
    onChange(option);
    setOpen(false);
    setSearch('');
  };

  const clear = (): void => {
    onChange(null);
    setSearch('');
  };

  const create = async (): Promise<void> => {
    if (!onCreate || !search.trim()) return;
    setCreating(true);
    try {
      const created = await onCreate(search.trim());
      pick(created);
    } finally {
      setCreating(false);
    }
  };

  const showCreate =
    onCreate &&
    search.trim().length > 0 &&
    !options.some((o) => o.label.toLowerCase() === search.trim().toLowerCase());
  const rowCount = options.length + (showCreate ? 1 : 0);

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, rowCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (stale) {
        // 立即查询：同名命中直接选中；否则把高亮移到"+ 新建"行，
        // 用户看见提示后再按一次 Enter 确认（避免防抖窗口内静默创建/错选）
        clearTimeout(debounceRef.current);
        void fetchOptions(search).then((opts) => {
          setOptions(opts);
          setStale(false);
          const exact = opts.find(
            (o) => o.label.toLowerCase() === search.trim().toLowerCase(),
          );
          if (exact) {
            pick(exact);
          } else if (onCreate && search.trim().length > 0) {
            setHighlighted(opts.length);
          } else {
            setHighlighted(0);
          }
        });
        return;
      }
      if (highlighted < options.length) {
        const opt = options[highlighted];
        if (opt) pick(opt);
      } else if (showCreate) {
        void create();
      }
    }
  };

  return (
    <div className="field" style={{ position: 'relative' }}>
      <div className="row" style={{ gap: 'var(--space-2)' }}>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={disabled}
          onClick={() => {
            setOpen((o) => !o);
            setHighlighted(0);
          }}
        >
          {value ? value.label : placeholder}
        </button>
        {value && !disabled && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={clear}
            aria-label={t('app.cancel')}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <>
          <div
            className="shell__warehouse-scrim"
            onClick={(e) => {
              /**
               * 【用户实测缺陷】Combobox 常被包在 <label class="field"> 里
               * （产品表单三个选择器都是）。遮罩虽然视觉上盖满全屏，DOM 上
               * 仍在 label 内——点击 label 里的非交互元素，浏览器默认行为会
               * 把点击转发给 label 的第一个可标记控件（开关按钮），于是
               * "关闭 + 再切换 = 菜单永远关不上"。preventDefault 掐断转发。
               */
              e.preventDefault();
              setOpen(false);
            }}
          />
          <div
            className="shell__warehouse-menu"
            style={{ width: '320px', maxHeight: '320px', overflowY: 'auto' }}
            onClick={(e) => {
              // 同上：菜单自身也在 label 里，点菜单内部留白（非选项）会被
              // 转发给开关按钮而意外关闭菜单
              e.preventDefault();
            }}
          >
            <input
              autoFocus
              placeholder={t('app.search')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setHighlighted(0);
                // 同步置脏（不能靠 effect——effect 在绘制后才跑，
                // 输入后立刻按 Enter 会读到旧的 stale=false 而错选旧选项）
                setStale(true);
              }}
              onKeyDown={onKeyDown}
              style={{ marginBottom: 'var(--space-2)' }}
            />
            {options.map((option, i) => (
              <button
                key={option.id}
                type="button"
                className="shell__warehouse-menu-item"
                aria-checked={value?.id === option.id}
                style={
                  i === highlighted ? { background: 'var(--color-surface-sunken)' } : undefined
                }
                onClick={() => pick(option)}
                onMouseEnter={() => setHighlighted(i)}
              >
                {option.label}
              </button>
            ))}
            {options.length === 0 && !showCreate && (
              <p className="field-hint">{t('empty.noResults')}</p>
            )}
            {showCreate && (
              <button
                type="button"
                className="shell__warehouse-menu-item"
                disabled={creating}
                style={
                  options.length === highlighted
                    ? { background: 'var(--color-surface-sunken)' }
                    : undefined
                }
                onClick={() => void create()}
              >
                {t('presets.createNamed', { name: search.trim() })}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
